import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { SkeletonBase, Text, toast } from "@foris/avocado-suite";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { dataLeagues } from "./utils";
import {
  ContextVacancies,
  IContextVacancies,
  useContextVacancies,
} from "./contextVacancies/ContextVacancies";
import { Events, useListenEvent } from "@common/hooks/useListenEvent";
import Layout from "../../common/layout/Layout";
import Header from "./Header";
import Groups from "./Groups";
import Instructor from "./Instructor";
import bundlesCubeGraphql from "./graphql/bundlesCube.query.graphql";
import { Bundle } from "../../models/ISchema";
import { IParams } from "../../models/IParams";
import DimensionsSearchSkeleton from "@components/dimensions-search-skeleton";
import css from "./vacancies.module.scss";
import "react-tabs/style/react-tabs.css";
import { useBundleEditAbilities } from "./hooks/useBundleEditAbilities";
import InstructorCorrectionGuard from "./InstructorCorrectionGuard";
import Modal from "@components/modal";
import {
  isSameInstructorRemovalContext,
  resetInstructorRemovalFlow,
  setInstructorRemovalFlowState,
  useInstructorRemovalFlowStore,
} from "@context/instructorRemovalFlow/useInstructorRemovalFlowStore";
import { getInstructorCorrectionValidity } from "@context/instructorRemovalFlow/classifyInstructorRemoval";
import useNavigationUrl from "@common/hooks/useNavigationUrl";

export enum VacanciesType {
  instructor = "instructor",
  vacancies = "vacancies",
}

const Vacancies: React.FC<any> = () => {
  const { workspace, origin, scenario, id }: IParams = useParams();
  const [dataBundle, setDataBundle] = useState<Bundle>(null);
  const context: IContextVacancies = useContextVacancies();
  const [activeEditVacancies, setActiveEditVacancies] = useState(false);
  const [activeEditInstructor, setActiveEditInstructor] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [showDepartureWarning, setShowDepartureWarning] = useState(false);
  const [correctionDataError, setCorrectionDataError] = useState(false);
  const history = useHistory();
  const { editAssignmentUrl } = useNavigationUrl();
  const removalStatus = useInstructorRemovalFlowStore(state => state.status);
  const removalContext = useInstructorRemovalFlowStore(state => state.context);
  const { canEditVacancies } = useBundleEditAbilities(dataBundle);

  const contextIdentity = {
    workspaceId: String(workspace),
    scenarioId: String(scenario),
    originId: String(origin),
    linkId: String(removalContext?.linkId),
    bundleId: String(id),
  };
  const belongsToCurrentBundle =
    !!removalContext &&
    isSameInstructorRemovalContext(removalContext, contextIdentity) &&
    String(removalContext.bundleId) === String(id);
  const correctionIsActive =
    belongsToCurrentBundle && ["correcting-bundle", "saving-correction"].includes(removalStatus);
  const permissionWarningIsVisible = belongsToCurrentBundle && removalStatus === "warning-only";
  const replacementWarningIsVisible =
    belongsToCurrentBundle && removalStatus === "needs-replacement";

  const { t } = useTranslation();

  const [getData, { loading }] = useLazyQuery(bundlesCubeGraphql, {
    variables: {
      scenarioId: scenario,
      originId: origin,
      bundleId: id,
    },
    fetchPolicy: "no-cache",
    onError: error => {
      console.error("[InstructorCorrection] Bundle query failed", error);
      if (correctionIsActive) setCorrectionDataError(true);
    },
    onCompleted: data => {
      const leagueBase: Bundle = data.cube.bundle;
      if (!leagueBase) {
        console.error("vacancies data");
        if (correctionIsActive) setCorrectionDataError(true);
        return;
      }

      setCorrectionDataError(false);
      setDataBundle(leagueBase);

      const flow = useInstructorRemovalFlowStore.getState();
      if (flow.status === "saving-correction" && String(flow.context?.bundleId) === String(id)) {
        const target = leagueBase.links?.find(link => String(link.id) === flow.context?.linkId);
        if (!target) {
          setInstructorRemovalFlowState({ status: "correcting-bundle" });
          setCorrectionDataError(true);
          return;
        }

        const validity = getInstructorCorrectionValidity({
          instructors: (target.instructorResponsability ?? []).map(instructorLink => ({
            instructorLinkId: String(instructorLink.id),
            instructorId: String(instructorLink.instructor.id),
            responsibility: Number(instructorLink.teachingHoursPercentage ?? 0),
            isPrimary: !!instructorLink.isPrimary,
          })),
        });

        if (validity.valid) {
          resetInstructorRemovalFlow();
          setActiveEditInstructor(false);
          toast.success(t("bundle.notifications.success-changes"), { autoClose: 3000 });
        } else {
          setInstructorRemovalFlowState({ status: "correcting-bundle" });
        }
      }
    },
  });

  const handleFetchInitialData = () => {
    setCorrectionDataError(false);
    setDataBundle(null);
    context.setCurrentLeague(null);

    // call on next tick to avoid race condition
    const timeoutId = setTimeout(() => {
      getData();
      clearTimeout(timeoutId);
    }, 0);
  };

  useEffect(() => {
    handleFetchInitialData();
  }, [id]);

  useListenEvent({
    eventKey: "on:refresh",
    handler: (detail: Events["on:refresh"]) => {
      if (detail?.target === "bundle-view") {
        handleFetchInitialData();
      }
    },
  });

  useEffect(() => {
    if (dataBundle) {
      const formatData = dataLeagues(dataBundle);
      if (formatData) context.setCurrentLeague(formatData);
      else console.error("vacancies format");
    }
  }, [dataBundle]);

  useEffect(() => {
    if (!belongsToCurrentBundle || !dataBundle) return;

    setSelectedTab(1);
    const target = dataBundle.links?.find(link => String(link.id) === removalContext?.linkId);
    const hasEffectivePermission = canEditVacancies && !!target?.course?.edhCanEdit;

    if (!target) {
      setCorrectionDataError(true);
      return;
    }

    if (!target.instructorResponsability?.length) {
      setActiveEditInstructor(false);
      setInstructorRemovalFlowState({
        status: "needs-replacement",
        pendingReplacement: true,
      });
      return;
    }

    if (!hasEffectivePermission) {
      setActiveEditInstructor(false);
      setInstructorRemovalFlowState({ status: "warning-only" });
      return;
    }

    if (removalStatus === "correcting-bundle") setActiveEditInstructor(true);
  }, [belongsToCurrentBundle, dataBundle, canEditVacancies, removalStatus, removalContext?.linkId]);

  return (
    <Layout contextSearch>
      <InstructorCorrectionGuard
        active={correctionIsActive}
        showWarning={showDepartureWarning}
        onBlocked={() => setShowDepartureWarning(true)}
        onCloseWarning={() => setShowDepartureWarning(false)}
      />

      <Modal
        typeState="warning"
        title={t("bundle.instructor-correction.permission-title")}
        show={permissionWarningIsVisible}
        textButtonPrincipal={t("bundle.instructor-correction.acknowledge-action")}
        onClickPrincipal={resetInstructorRemovalFlow}
      >
        <Text>{t("bundle.instructor-correction.permission-message")}</Text>
      </Modal>

      <Modal
        typeState="warning"
        title={t("bundle.instructor-correction.replacement-title")}
        show={replacementWarningIsVisible}
        textButtonPrincipal={t("bundle.instructor-correction.replacement-action")}
        onClickPrincipal={() => {
          setInstructorRemovalFlowState({
            status: "editing-assignment",
            beforeSnapshot: null,
            afterSnapshot: null,
            reasons: [],
            pendingReplacement: true,
          });
          history.push(editAssignmentUrl(removalContext?.linkId));
        }}
      >
        <Text>{t("bundle.instructor-correction.replacement-message")}</Text>
      </Modal>

      <Modal
        typeState="error"
        title={t("bundle.instructor-correction.load-error-title")}
        show={correctionIsActive && correctionDataError}
        textButtonPrincipal={t("bundle.instructor-correction.retry-action")}
        onClickPrincipal={handleFetchInitialData}
      >
        <Text>{t("bundle.instructor-correction.load-error-message")}</Text>
      </Modal>

      {!!(loading || !dataBundle || !context.currentLeague) && (
        <div className={cx(css.cntVacancies)}>
          <Header isLoading />

          <div className={css.contentBundle}>
            <div className={css.skeletonTabs}>
              <SkeletonBase width={78} height={29} />
              <SkeletonBase width={101} height={29} />
            </div>

            <div className={css.skeletonLinkCard}>
              <div className={css.skeletonLinkCard_header}>
                <SkeletonBase width="100%" height={48} radius="8px 8px 0 0" />
              </div>

              <div className={css.skeletonLinkCard_content}>
                <div className={css.skeletonGroups}>
                  <SkeletonBase width={39} height={14} />
                  <DimensionsSearchSkeleton
                    columns={8}
                    rows={2}
                    showHeader={false}
                    showPagination={false}
                  />
                </div>

                <div className={css.skeletonComponents}>
                  <SkeletonBase width={50} height={14} />
                  <DimensionsSearchSkeleton
                    columns={4}
                    rows={2}
                    showHeader={false}
                    showPagination={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !!dataBundle && !!context.currentLeague && (
        <ContextVacancies.Provider value={context}>
          <article className={cx(css.cntVacancies)}>
            <Header data={dataBundle} />
            <section className={css.contentBundle}>
              <Tabs
                selectedIndex={selectedTab}
                onSelect={index => {
                  if (correctionIsActive && index !== 1) {
                    setShowDepartureWarning(true);
                    return false;
                  }
                  setSelectedTab(index);
                  return true;
                }}
              >
                <TabList className={css.tab}>
                  <Tab className={css.tab_item}>{t("bundle.tabs.groups")}</Tab>
                  <Tab className={css.tab_item}>{t("bundle.tabs.instructors")}</Tab>
                </TabList>
                <TabPanel className={css.tabPanel}>
                  <Groups
                    active={activeEditVacancies}
                    setActive={value => setActiveEditVacancies(value)}
                    dataBundle={dataBundle}
                  />
                </TabPanel>
                <TabPanel className={css.tabPanel}>
                  <Instructor
                    active={activeEditInstructor}
                    setActive={value => setActiveEditInstructor(value)}
                    dataBundle={dataBundle}
                    onBlockedExit={
                      correctionIsActive ? () => setShowDepartureWarning(true) : undefined
                    }
                  />
                </TabPanel>
              </Tabs>
            </section>
          </article>
        </ContextVacancies.Provider>
      )}
    </Layout>
  );
};

export default Vacancies;
